import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FC, useState, useEffect } from 'react';
import { useJobseekerAddAssociationMutation, useJobseekerUpdateAssociationMutation } from '@carcareers/store';
import { useAuth } from '@carcareers/hooks';
import { JobseekerAssociation } from '@libs/models';
import { ApiError } from '@libs/types';

interface JobseekerAssociationModalProps {
  type: 'add' | 'edit';
  open: boolean;
  onClose: () => void;
  association?: JobseekerAssociation;
}

export const JobseekerAssociationModal: FC<JobseekerAssociationModalProps> = ({ type, open, onClose, association }) => {
  const { session } = useAuth();
  const [addAssociation, { isLoading: isAdding }] = useJobseekerAddAssociationMutation();
  const [updateAssociation, { isLoading: isUpdating }] = useJobseekerUpdateAssociationMutation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const [errors, setErrors] = useState<ApiError[]>([]);

  useEffect(() => {
    if (type === 'edit' && association) {
      setFormData({
        name: association.name || '',
        description: association.description || '',
      });
    } else if (type === 'add') {
      setFormData({
        name: '',
        description: '',
      });
    }
    setErrors([]);
  }, [type, association, open]);

  const getFieldError = (fieldName: string): string => {
    const fieldError = errors.find(error => error.property === fieldName);
    return fieldError?.errors?.[0] || '';
  };

  const hasFieldError = (fieldName: string): boolean => {
    return !!getFieldError(fieldName);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear errors for this field when user starts typing
    if (hasFieldError(field)) {
      setErrors(prev => prev.filter(error => error.property !== field));
    }
  };

  const handleSubmit = async () => {
    if (!session?.profile?.id) return;

    setErrors([]);

    try {
      if (type === 'add') {
        await addAssociation({
          jobseekerId: session.profile.id,
          name: formData.name,
          description: formData.description,
        }).unwrap();
      } else if (type === 'edit' && association) {
        await updateAssociation({
          id: Number(association.id),
          jobseekerId: Number(session.profile.id),
          name: formData.name,
          description: formData.description,
        }).unwrap();
      }

      if (type === 'add') {
        setFormData({
          name: '',
          description: '',
        });
      }
      onClose();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      }
    }
  };

  const isFormValid = formData.name && formData.description;
  const isLoading = isAdding || isUpdating;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{type === 'add' ? 'Add Association' : 'Edit Association'}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Association Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
            required
            placeholder="e.g., National Association of Software Developers"
            error={hasFieldError('name')}
            helperText={getFieldError('name')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={4}
            required
            placeholder="Describe your involvement, achievements, or role in this association"
            error={hasFieldError('description')}
            helperText={getFieldError('description')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isLoading}
          sx={{ borderRadius: 2 }}
        >
          {isLoading
            ? type === 'add'
              ? 'Adding...'
              : 'Updating...'
            : type === 'add'
            ? 'Add Association'
            : 'Update Association'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 